import { create } from 'zustand';

const useChatStore = create((set) => ({
    chats: [],
    messagesList: [],

    // Add a new chat
    addChat: (user) => set((state) => {
        // Check if the chat already exists for this user
        const existingChat = state.chats.find(u => u.userUid === user.userUid);

        // If the chat already exists, just open it
        if (existingChat) {
            const updatedChats = state.chats.map(u =>
                u.userUid === user.userUid ? { ...u, isOpen: true } : u
            );
            return { chats: updatedChats };
        }

        // If chat doesn't exist, add the new chat and make sure it is open
        const updatedChats = [...state.chats, { ...user, isOpen: true, inputValue: '', messages: [] }];

        // Ensure there are no more than 4 chats at a time
        if (updatedChats.length > 3) {
            updatedChats.shift(); // Remove the first (oldest) chat
        }

        return { chats: updatedChats };
    }),

    // Remove a chat
    removeChat: (userId) => set((state) => ({
        chats: state.chats.filter(user => user.userUid !== userId)
    })),

    // Toggle a chat (collapse/expand)
    toggleChat: (userId) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userId ? { ...user, isOpen: !user.isOpen } : user
        );
        return { chats: updatedChats };
    }),

    // Update the input value for a user
    updateInput: (userId, value) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userId ? { ...user, inputValue: value } : user
        );
        return { chats: updatedChats };
    }),

    // Send a message
    sendMessage: (userId, message) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userId
                ? { ...user, messages: [...user.messages, message], inputValue: '' }
                : user
        );
        return { chats: updatedChats };
    }),

    // Store messages if present
    storeMessages: (userId, messages) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userId
                ? { ...user, messages: messages.length > 0 ? messages : user.messages }
                : user
        );
        return { chats: updatedChats };
    }),

    // Set the message ID
    setMessageID: (userId) => set((state) => {
        // Find the corresponding message in messagesList
        const foundMessageListRecord = state.messagesList?.find(
            (message) => message.chatInfo.toUserUid === userId
        );

        // If no message is found, return the state as is
        if (!foundMessageListRecord) return state;

        // Update the msgID in chats where chatInfo.toUserUid matches userId
        const updatedChats = state.chats.map(user =>
            user.userUid === foundMessageListRecord.chatInfo.toUserUid
                ? { ...user, msgID: foundMessageListRecord.chatInfo.msgID }
                : user
        );

        return { chats: updatedChats };
    }),

    setMessagesList: (data) => set((state) => {
        const existingMessages = state.messagesList || [];

        // Merge new data with existing ones while removing duplicates based on chatID & msgID
        const updatedMessagesList = [
            ...existingMessages,
            ...data.filter(
                (newMsg) =>
                    !existingMessages.some(
                        (existingMsg) =>
                            existingMsg.chatID === newMsg.chatID &&
                            existingMsg.chatInfo.msgID === newMsg.chatInfo.msgID
                    )
            ),
        ];

        return { messagesList: updatedMessagesList };
    }),

    setMsgIDDirect: (userUid, msgID) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userUid ? { ...user, msgID: msgID } : user
        );
        return { chats: updatedChats };
    }),

    updateMessages: (userUid, updatedMessages) => set((state) => {
        const updatedChats = state.chats.map(user => {
            if (user.userUid === userUid) {
                // Check if there's already a message with the same chatID
                const messageExists = user.messages.some(message => message.chatID === updatedMessages.chatID);

                // If the message doesn't exist, add it to the messages array
                if (!messageExists) {
                    return {
                        ...user,
                        messages: [...user.messages, updatedMessages]  // Insert updatedMessages object at the end
                    };
                }
                // If the message already exists, return the user object unchanged
                return user;
            }
            return user;
        });
        return { chats: updatedChats };
    }),

    updateChatMessageByID: (message) => set((state) => {
        const currentUserUid = localStorage.getItem("userUid");

        const updatedChats = state.chats.map((chat) => {
            if (!chat.messages.some((msg) => msg.chatID === message.chatID)) {
                return chat;
            }

            const otherUserUid = chat.userUid;
            const isRelevantToThisChat =
                (message.senderUid === currentUserUid && message.receiverUid === otherUserUid) ||
                (message.senderUid === otherUserUid && message.receiverUid === currentUserUid);

            if (!isRelevantToThisChat) {
                return chat;
            }

            return {
                ...chat,
                messages: chat.messages.map((msg) => {
                    if (msg.chatID !== message.chatID) return msg;

                    const isSenderView =
                        message.senderUid === currentUserUid && message.receiverUid === otherUserUid;

                    const isReceiverViewForDeleteEveryone =
                        message.receiverUid === currentUserUid &&
                        message.senderUid === otherUserUid &&
                        message.isCompleteDeleted === true;

                    if (!isSenderView && !isReceiverViewForDeleteEveryone) {
                        return msg;
                    }

                    return {
                        ...msg,
                        isDeleted: true,
                        isCompleteDeleted: true,
                        msg: isSenderView ? message.sndrSideMsg : message.rcvrSideMsg,
                        isRead: false,
                    };
                }),
            };
        });

        return { chats: updatedChats };
    }),

    updateChatReadStatus: (message) => set((state) => {
        if (!message) return state;

        const currentUserUid = localStorage.getItem("userUid");

        const updatedChats = state.chats.map(chat => {
            const isRelevant =
                (message.senderUid === currentUserUid && message.receiverUid === chat.userUid) ||
                (message.receiverUid === currentUserUid && message.senderUid === chat.userUid);

            if (!isRelevant) return chat;

            const updatedMessages = chat.messages.map(msg =>
                msg.chatID === message.chatID ? { ...msg, isRead: true } : msg
            );

            return { ...chat, messages: updatedMessages };
        });

        return { chats: updatedChats };
    }),


    updateAllChatReadStatus: (messages) => set((state) => {
        const currentUserUid = localStorage.getItem("userUid");
        const payload = Array.isArray(messages) ? messages : [messages];

        if (!payload.length) return state;

        const hasChatIds = payload.some((msg) => msg?.chatID != null);

        if (!hasChatIds) {
            const data = payload[0];
            const partnerUid =
                data?.fromUserUid ??
                data?.readerUid ??
                data?.readByUid ??
                (String(data?.toUserUid) === String(currentUserUid)
                    ? data?.senderUid
                    : data?.toUserUid);

            if (!partnerUid) return state;

            return {
                chats: state.chats.map((chat) => {
                    if (String(chat.userUid) !== String(partnerUid)) return chat;
                    return {
                        ...chat,
                        messages: chat.messages.map((msg) => {
                            if (msg.isOwned === false) return msg;
                            return { ...msg, isRead: true };
                        }),
                    };
                }),
            };
        }

        const partnerUids = [
            ...new Set(
                payload
                    .flatMap((msg) => [msg.senderUid, msg.receiverUid])
                    .filter((uid) => uid && String(uid) !== String(currentUserUid))
            ),
        ];

        const userChatExists = state.chats.some((chat) =>
            partnerUids.some((uid) => String(chat.userUid) === String(uid))
        );
        if (!userChatExists) return state;

        const updatedChats = state.chats.map((chat) => {
            const matchingMessages = payload.filter(
                (msg) =>
                    (String(msg.senderUid) === String(currentUserUid) &&
                        String(msg.receiverUid) === String(chat.userUid)) ||
                    (String(msg.receiverUid) === String(currentUserUid) &&
                        String(msg.senderUid) === String(chat.userUid))
            );

            if (matchingMessages.length === 0) return chat;

            const readChatIds = new Set(
                matchingMessages.map((m) => String(m.chatID))
            );

            const updatedMessages = chat.messages.map((msg) => {
                if (!readChatIds.has(String(msg.chatID))) return msg;
                if (msg.isOwned === false) return msg;
                return { ...msg, isRead: true };
            });

            return { ...chat, messages: updatedMessages };
        });

        return { chats: updatedChats };
    }),

    clearAllChats: () => set(() => ({
        chats: []
    })),
}));

export default useChatStore;
